// utils/formatters.ts
/*///////////////////////////////////////////////////////////////////////////////////////////////////
//// 숫자 포맷터 유틸리티
//// 사용 예시
///   formatNumber(1234567, { format: '###,###' }); // "1,234,567"
///   formatNumber(1234567.89, { format: '###,###.00' }); // "1,234,567.89"
///   formatNumber(1234567.89, { format: '###.##', suffix: '원' }); // "1,234567.89원"
////
///////////////////////////////////////////////////////////////////////////////////////////////////*/
import type { KeyboardEvent } from 'react';
import { MenuItem } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import AgGridDateCellEditor from '../components/AgGridDateCellEditor';
import { getUserInfo } from './userUtils';

export type NumberStyle = 'decimal' | 'currency' | 'percent';

/*///////////////////////////////////////////////////////////////////////////////////////////////////
//// toDayjs() : MUI DatePicker value용 dayjs 객체 캐시 변환
////  - 화면 곳곳에서 `contents.xxx ? dayjs(contents.xxx) : null` 형태로 매 렌더마다 새 dayjs 객체를
////    만들면, 그 값이 실제로는 안 바뀌었어도 레퍼런스가 계속 달라져서 DatePicker가 "값이 바뀐 것"으로
////    오인함. 특히 옆에 SunEditor 등 자주 리렌더되는 요소가 있으면, 달력 팝업에서 이전 달로 넘겨
////    날짜를 고르려는 도중에 리렌더가 끼어들어 달력이 현재 값의 월로 되돌아가버리는 문제가 생김.
////  - 동일한 (value, format) 조합에는 항상 같은 dayjs 인스턴스를 반환하도록 캐시해서 이 문제를 근본적으로 막음.
//// 사용 예시
///   value={toDayjs(contents.expire_dt)}
///   value={toDayjs(filters.cls_yymm_h, 'YYYY-MM')}
///////////////////////////////////////////////////////////////////////////////////////////////////*/
const dayjsCache = new Map<string, Dayjs>();
export function toDayjs(value: string | null | undefined, format?: string): Dayjs | null {
  if (!value) return null;
  const key = format ? `${format}::${value}` : value;
  let cached = dayjsCache.get(key);
  if (!cached) {
    cached = format ? dayjs(value, format) : dayjs(value);
    dayjsCache.set(key, cached);
  }
  return cached;
}

/*///////////////////////////////////////////////////////////////////////////////////////////////////
//// onEnterQuery() : 조건부영역(conditionSection)에서 Enter키로 조회 실행
////  - 조건영역 안의 컨트롤(TextField/Select/DatePicker 등) 각각에 onKeyDown을 다는 대신, 조건영역
////    컨테이너 div 하나에만 걸면 keydown이 버블링되어 그 안의 모든 컨트롤에 공통 적용됨.
////  - textarea(여러 줄 입력)는 Enter가 줄바꿈이어야 하므로 제외함.
//// 사용 예시
///   <div className='conditionSection' onKeyDown={onEnterQuery(fncQuery)}>
///////////////////////////////////////////////////////////////////////////////////////////////////*/
export const onEnterQuery = (fncQuery: () => void) => (e: KeyboardEvent) => {
  if (e.key !== 'Enter') return;
  if ((e.target as HTMLElement)?.tagName === 'TEXTAREA') return;
  fncQuery();
};

export interface FormatNumberOptions {
  locale?: string;
  style?: 'decimal' | 'currency' | 'percent';
  currency?: string;
  format?: string; // 예: "###,###.00"
  prefix?: string;
  suffix?: string;
  unit?: string;
  showPlusSign?: boolean;
  negativePrefix?: string;
  negativeSuffix?: string;
  hideZero?: boolean; // 0인경우 보이기/숨기기
}

function parseFormatString(format?: string): Intl.NumberFormatOptions {
  if (!format) return {};

  const useGrouping = format.includes(',');
  const decimalMatch = format.match(/\.(#+|0+)/);
  const fractionDigits = decimalMatch ? decimalMatch[1].length : 0;

  return {
    useGrouping,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  };
}

// Grid에서만 숫자값을 포맷팅하는 함수
export const formatNumber = (
  value: number | null | undefined,
  options: FormatNumberOptions = {}
): string => {
  const {
    locale = 'ko-KR',
    style = 'decimal',
    currency,
    format,
    prefix = '',
    suffix = '',
    unit = '',
    showPlusSign = false,
    negativePrefix = '-',
    negativeSuffix = '',
    hideZero = true,
  } = options;

  if (value == null || isNaN(value)) return '0';
  if (hideZero && value === 0) return '0';

  const isNegative = value < 0;
  const absValue = Math.abs(value);

  const formatOptions = parseFormatString(format);
  const formatter = new Intl.NumberFormat(locale, {
    style,
    currency,
    ...formatOptions,
  });

  const formatted = formatter.format(absValue);
  const finalPrefix = isNegative ? negativePrefix : showPlusSign ? '+' : prefix;
  const finalSuffix = isNegative ? negativeSuffix : suffix;

  return `${finalPrefix}${formatted}${unit}${finalSuffix}`;
};

// 날짜값을 yyyy-MM-dd로 변환하는 함수
export const convertDate = (dateStr: string | undefined, format: string) => {
  if (!dateStr) return '';
  // ISO 문자열 또는 Date 객체 모두 처리
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  if (format === 'yyyy-MM-dd') return d.toISOString().slice(0, 10);
  if (format === 'yyyyMMdd') return d.toISOString().slice(0,4) + '-' + d.toISOString().slice(4,6) + '-' + d.toISOString().slice(6,8);
  if (format === 'yyyy/MM/dd') return d.toISOString().slice(0,4) + '-' + d.toISOString().slice(5,7) + '-' + d.toISOString().slice(8,10);
  if (format === 'MM/dd/yyyy') return d.toISOString().slice(6,10) + '-' + d.toISOString().slice(0,2) + '-' + d.toISOString().slice(3,5);
  if (format === 'MM-dd-yyyy') return d.toISOString().slice(6,10) + '-' + d.toISOString().slice(0,2) + '-' + d.toISOString().slice(3,5);
  if (format === 'dd/MM/yyyy') return d.toISOString().slice(6,10) + '-' + d.toISOString().slice(3,5) + '-' + d.toISOString().slice(8,10);

  return d.toISOString().slice(0, 10);
};

// ISO 또는 Date를 "yyyy-MM-ddTHH:mm"으로 변환
export const convertDateTime = (dateStr: string | undefined) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  // pad 함수
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 유틸명 : getCurrentLanguage()
 ** 저장된 현재 언어를 가져옵니다.
 ** 저장소 : localStorage의 'userInfo'(로그인정보) 안의 language 필드 - mainFrame에서 언어 전환 시
 **         setLocalStorage()로 이 값도 함께 갱신되므로, 여기서 그대로 읽으면 항상 최신 언어를 얻음.
 **         (예전엔 존재하지 않는 localStorage 키 'language'를 직접 읽어서 항상 null만 반환하던 버그가 있었음 -
 **          dateColDef(type)처럼 language 인자를 생략했을 때 표시가 항상 'ko' 고정으로 보이던 원인)
 * @returns 저장된 언어(language) 또는 null
 */
export const getCurrentLanguage = (): string | null => {
  try {
    return getUserInfo()?.language || null;
  } catch (error) {
    console.error('Failed to get current language:', error);
    return null;
  }
};

// pgmIdOf : 호출프로그램ID(ba/K1001M1) -> 화면ID(K1001M1). 구현은 utils/pgmIdUtil.ts(AgGrid 등 공용 컴포넌트가 순환참조 없이 쓰도록 분리)
export { pgmIdOf } from './pgmIdUtil';

/** 유틸명 : getDateFormat()
 * 날짜 형식을 변환합니다.
 * @param pFormat 변환할 날짜 형식
 *  YYYY-MM-DD, YY-MM-DD, YYYY-MM-DD HH:MM:SS, YYYYMMDD, YYMMDD, YYYYMMDDHHMMSS
 * @returns 변환된 날짜 문자열
 */
export const getDateFormat = (pFormat: string): string => {
  const today = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const y = today.getFullYear();
  const m = pad(today.getMonth() + 1);
  const d = pad(today.getDate());
  const hh = pad(today.getHours());
  const mm = pad(today.getMinutes());
  const ss = pad(today.getSeconds());
  if (pFormat === 'YYYY-MM-DD') return `${y}-${m}-${d}`;
  if (pFormat === 'YY-MM-DD') return `${String(y).slice(-2)}-${m}-${d}`;
  if (pFormat === 'YYYY-MM-DD HH:MM:SS') return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
  if (pFormat === 'YYYYMMDD') return `${y}${m}${d}`;
  if (pFormat === 'YYMMDD') return `${String(y).slice(-2)}${m}${d}`;
  if (pFormat === 'YYYYMMDDHHMMSS') return `${y}${m}${d}${hh}${mm}${ss}`;
  else return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
};

// 추가 유틸리티 함수가 필요하면 여기에 작성

/** 유틸명: hasChanges
 * 싱글화면에서 사용함
 * - 신규(C) : 바로 Return true
 * - 수정(U) : 변경된 자료가 있는지 확인
 * - 삭제(D) : 해당유틸 호출하지 않음, fncDelete -> fetchDBDelete로 처리됨.
 * contents와 contents._origin을 비교하여 값 변경 여부를 반환합니다.
 * - _origin이 없으면 변경된 것으로 간주(true)
 * - 제외 키는 비교에서 제외합니다.
 */
export const hasChanges = (
  contents: Record<string, any>,
  options?: { excludeKeys?: string[] }
): boolean => {
  const origin = contents?._origin;
  // origin이 없으면 신규/미초기화로 보고 변경된 것으로 처리
  if (!origin) return true;

  const defaultExclude = [
    'char_cud', '_origin',
    'no', 'rowKey',
    'insrt_user_id', 'insrt_dt', 'updt_user_id', 'updt_dt'
  ];
  const exclude = new Set<string>([
    ...defaultExclude,
    ...(options?.excludeKeys ?? [])
  ]);

  const normalize = (v: any) => {
    if (v === null || v === undefined) return '';
    return String(v).trim();
  };

  // 양쪽 키의 합집합을 비교하여 어느 하나라도 다르면 변경됨
  const keys = new Set<string>([
    ...Object.keys(origin || {}),
    ...Object.keys(contents || {})
  ]);

  for (const key of keys) {
    if (exclude.has(key)) continue;
    const a = normalize(origin[key]);
    const b = normalize(contents[key]);

    if (a !== b) return true;
  }
  return false;
};

/**
 * ag-Grid 컬럼(ColDef) 배열의 headerName을 DD(ZA_DD) 번역으로 자동 치환합니다.
 *  - 기본적으로 field를 DD의 OBJ_ID로 그대로 사용해서 t(field)를 조회합니다.
 *  - field명과 DD 키가 다른 경우(예: 뷰 컬럼이라 field가 'vw_seq'인데 DD 키는 'seq_no')는
 *    컬럼에 ddKey를 따로 적어주면 그걸 우선 사용합니다.
 *  - gridId: ZA_DD의 실제 키(PGM_ID+OBJ_ID+GRID_ID)에서 GRID_ID를 지정합니다. 한 화면 안에
 *    여러 그리드가 같은 OBJ_ID를 다른 뜻으로 쓰는 경우(예: agGrid1의 'remark' vs 단일필드의 'remark')를
 *    구분하기 위함 - 그리드1 컬럼이면 'agGrid1'처럼 그 그리드 변수명을 그대로 넘기면 됩니다.
 *  - t(조회키, undefined, gridId) 결과가 있으면 그 값으로 교체하고, 없으면 배열에 적어둔 headerName(한글 기본값) 그대로 사용.
 *  - 사용법: 각 컬럼에서 t('key') || '한글' 을 매번 안 써도, headerName에 한글만 적고
 *           컬럼 배열 전체를 withDDHeader(cols, t, gridId)로 한 번만 감싸면 됩니다.
 *    예) const agGrid1Cols = useMemo<ColDef[]>(() => withDDHeader([
 *          { headerName: '플래너ID', field: 'pl_id', width: 110 },
 *          { headerName: '순번', field: 'vw_seq', ddKey: 'seq_no', width: 90 }, // field !== DD키인 경우
 *          ...
 *        ], t, 'agGrid1'), [t]);
 */
export const withDDHeader = <T extends { field?: string; headerName?: string; ddKey?: string }>(
  cols: T[],
  t: (key: string, params?: Record<string, string | number>, gridId?: string) => string,
  gridId?: string
): T[] => {
  return cols.map(col => {
    const lookupKey = col.ddKey || col.field;
    // ddKey는 이 함수가 소비하는 내부 지시자일 뿐 ag-Grid colDef 표준 속성이 아니므로, 최종 결과에는 남기지 않음
    // (top-level에 그대로 두면 "AG Grid: invalid colDef property 'ddKey'" 경고가 남).
    const { ddKey, ...rest } = col as T & { ddKey?: string };
    return lookupKey ? ({ ...rest, headerName: t(lookupKey, undefined, gridId) || col.headerName } as T) : (rest as T);
  });
};

/**
 * 콤보(useComboBox/useComboBoxSite 등)로 받은 옵션 목록을 ag-Grid 컬럼의 select 에디터로 붙일 때
 * 매번 반복되던 cellEditor/cellEditorParams/valueFormatter 3줄을 한 번에 만들어줍니다.
 *  - excludeValues: 검색조건과 콤보를 공유할 때 섞여있는 '전체(%)' 같은 sentinel값은 그리드 실제
 *    데이터값으로 저장되면 안 되므로 기본으로 제외합니다. 필요하면 두 번째 인자로 바꿀 수 있습니다.
 *  - select 에디터는 목록 밖의 값을 고를 방법이 없어 Delete키로 못 지우므로, 목록 맨 앞에 빈 항목을
 *    항상 추가합니다 - 그 빈 항목을 선택하면 값을 지울 수 있습니다. 필수 컬럼은 required:true +
 *    getMissingRequiredField()(저장 시 검증)로 막히므로 빈 항목이 있어도 빈 채로 저장되지는 않습니다.
 *  - 사용법: { headerName: '보험사분류코드', field: 'bp_grp', editable: true, ...comboColDef(cbBpGrp) }
 */
export const comboColDef = (
  options: { value: any; label: string }[],
  excludeValues: any[] = ['%']
) => {
  const filtered = (options || []).filter(o => !excludeValues.includes(o.value));
  const labelMap = new Map((options || []).map(o => [String(o.value), o.label]));
  // 명칭(라벨) -> 값(코드) 역방향 조회 - AgGrid 복사가 useFormatter:true라 콤보 셀은 코드가 아니라 명칭이
  // 복사되므로(예: 'S' 대신 '시스템'), 붙여넣기 쪽(AgGrid.tsx pasteTextToGrid)이 이걸로 명칭을 코드로
  // 되돌려 받아들일 수 있게 함(2026-10-02).
  const valueByLabel: Record<string, any> = {};
  for (const o of (options || [])) valueByLabel[o.label] = o.value;
  return {
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: { values: ['', ...filtered.map(o => o.value)], valueByLabel },
    valueFormatter: (params: any) => labelMap.get(String(params.value)) ?? (params.value ?? ''),
    // 기본 텍스트 필터는 valueFormatter(화면표시)가 아니라 실제 셀 값(코드)을 대상으로 매칭하므로,
    // "장기"처럼 명칭으로 쳐도 안 걸리고 "L"처럼 코드로 쳐야만 걸리는 문제가 있었음. 필터 전용으로
    // 코드를 명칭으로 변환한 값을 돌려줘서, 화면에 보이는 명칭 그대로 검색 가능하게 함.
    filterValueGetter: (params: any) => {
      const field = params.colDef?.field;
      const raw = field ? params.data?.[field] : undefined;
      return labelMap.get(String(raw)) ?? (raw ?? '');
    },
  };
};

/**
 * 콤보로 구동되는 필수(required) 컬럼의 기본값 - 콤보의 '전체(%)' 같은 sentinel값을 제외한 첫번째 옵션의 값을 반환합니다.
 *  - 콤보가 아직 로딩 전(옵션 배열이 비어있음)이면 ''을 반환 - 로딩 후 화면을 다시 열면(행추가 시점) 정상값이 채워짐.
 *  - 사용법(행추가 시 기본값): const newGrid1RowData = { usage_flg: comboFirstValue(cbYN) };
 */
export const comboFirstValue = (
  options: { value: any; label: string }[],
  excludeValues: any[] = ['%']
): any => {
  return (options || []).find(o => !excludeValues.includes(o.value))?.value ?? '';
};

/**
 * dateColDef에서 쓰는 날짜 타입.
 *  - 'yyyy-mm-dd'/'yyyy-mm' : 언어별 표기 관례로 화면표시(useDatePickerLocale과 동일한 매핑),
 *    값 저장은 항상 ISO('yyyy-MM-dd'/'yyyy-MM')로 고정 - 백엔드 계약 유지.
 *  - 'yyyymmdd'/'yyyymm'/'yyyy' : 구분자 없는 고정 자릿수 - 언어 무관, 값도 이 형식 그대로 저장(레거시 CHAR(8) 등).
 *  - 'yyyy-mm-dd hh:mm' : 날짜+시간(24시간, 분 단위) - 화면표시는 언어별 날짜 표기 + ' HH:mm', 값 저장은 항상 'yyyy-MM-dd HH:mm'.
 */
export type DateColDefType = 'yyyy-mm-dd' | 'yyyy-mm' | 'yyyymmdd' | 'yyyymm' | 'yyyy' | 'yyyy-mm-dd hh:mm';

// 언어코드 -> 화면표시 포맷 (useDatePickerLocale.ts의 DATE_FORMAT_MAP/MONTH_FORMAT_MAP과 동일한 국가별 표기 관례)
const DATE_DISPLAY_BY_LANG: Record<string, string> = { ko: 'yyyy-MM-dd', en: 'MM/dd/yyyy', jp: 'yyyy/MM/dd', zh: 'yyyy-MM-dd', vn: 'dd/MM/yyyy' };
const MONTH_DISPLAY_BY_LANG: Record<string, string> = { ko: 'yyyy-MM', en: 'MM/yyyy', jp: 'yyyy/MM', zh: 'yyyy-MM', vn: 'MM/yyyy' };
// 타입별 저장(값) 포맷 - 언어와 무관하게 고정
const STORAGE_PATTERN_BY_TYPE: Record<DateColDefType, string> = {
  'yyyy-mm-dd': 'yyyy-MM-dd',
  'yyyy-mm': 'yyyy-MM',
  yyyymmdd: 'yyyyMMdd',
  yyyymm: 'yyyyMM',
  yyyy: 'yyyy',
  'yyyy-mm-dd hh:mm': 'yyyy-MM-dd HH:mm',
};

// 'yyyy-MM-dd' 뿐 아니라 압축형(yyyyMMdd/yyyyMM/yyyy)도 알아보고 Date로 변환 - Date 생성자는 압축형을 못 읽으므로 직접 분해
function parseFlexibleDate(raw: any): Date | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const str = String(raw).trim();
  let d: Date;
  // 'yyyy-MM-dd HH:mm(:ss)' (날짜+시간, 공백 구분 - 서버 조회값/편집기값) - 시간대 변환 없이 로컬 시각 그대로 해석
  // (ISO 'T...Z' 값은 여기에 걸리지 않고 아래 기존 처리로 감)
  const dt = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})(?::(\d{2}))?$/.exec(str);
  if (dt) d = new Date(Number(dt[1]), Number(dt[2]) - 1, Number(dt[3]), Number(dt[4]), Number(dt[5]), Number(dt[6] || 0));
  else if (/^\d{8}$/.test(str)) d = new Date(`${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`);
  else if (/^\d{6}$/.test(str)) d = new Date(`${str.slice(0, 4)}-${str.slice(4, 6)}-01`);
  else if (/^\d{4}$/.test(str)) d = new Date(`${str}-01-01`);
  else d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

function formatDateByPattern(raw: any, pattern: string): string {
  const d = parseFlexibleDate(raw);
  if (!d) return raw ? String(raw) : '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return pattern
    .replace('yyyy', String(d.getFullYear()))
    .replace('MM', pad(d.getMonth() + 1))
    .replace('dd', pad(d.getDate()))
    .replace('HH', pad(d.getHours()))
    .replace('mm', pad(d.getMinutes()));
}

/**
 * 그리드 컬럼을 날짜 편집(언어별 달력 팝업)으로 붙일 때 매번 반복되던 cellEditor/valueGetter/valueFormatter/
 * valueSetter를 한 번에 만들어줍니다.
 *  - 편집기는 AgGridDateCellEditor(조건부영역의 MUI DatePicker+useDatePickerLocale을 그대로 재사용한 커스텀
 *    셀 에디터) - 브라우저 네이티브 날짜입력과 달리 앱 언어를 그대로 따라감(한/영/일/중/베 달력 문구+표기순서).
 *  - dateColDef(language, 'yyyy-mm-dd') / dateColDef(language, 'yyyy-mm') : 언어별 표기 관례로 화면표시+팝업.
 *  - dateColDef(language, 'yyyymmdd') / dateColDef(language, 'yyyymm') / dateColDef('yyyy') : 구분자 없는
 *    고정 자릿수(표시는 언어 무관, 팝업은 년/월만 또는 년만 선택하도록 좁혀짐).
 *  - language를 생략하면(예: dateColDef('yyyy-mm-dd')) getCurrentLanguage()로 현재 앱 언어를 자동 감지합니다.
 *    화면에서 언어를 바꾸면 ddKey가 다시 생성되고, 컬럼정의 useMemo는 보통 이미 ddKey를 의존성으로 갖고 있어
 *    (withDDHeader(cols, ddKey, ...)) 자동으로 재계산되므로 language를 의존성 배열에 따로 안 넣어도 됩니다.
 *  - 저장된 원본값이 어떤 형식(ISO 풀데이트, 압축형 등)이든 valueGetter가 먼저 ISO로 통일해서 편집기/화면표시에
 *    넘기고, 실제 입력이 끝나면 valueSetter가 타입에 맞는 저장형식으로 다시 변환해서 행에 반영합니다.
 *  - 사용법: { field: 'valid_dt_fr', editable: true, ...dateColDef('yyyy-mm-dd') }              // 언어 자동감지
 *           { field: 'valid_dt_fr', editable: true, ...dateColDef(language, 'yyyy-mm-dd') }     // 언어 명시
 *           { field: 'ext_ym', editable: true, ...dateColDef('yyyymm') }
 */
export const dateColDef = (languageOrType?: string, maybeType?: DateColDefType) => {
  const hasLanguage = maybeType !== undefined;
  const type: DateColDefType = (hasLanguage ? maybeType : (languageOrType as DateColDefType | undefined)) || 'yyyy-mm-dd';
  const language = (hasLanguage ? languageOrType : undefined) || getCurrentLanguage() || 'ko';

  const displayPattern =
    type === 'yyyy-mm-dd' ? (DATE_DISPLAY_BY_LANG[language] || DATE_DISPLAY_BY_LANG.ko) :
    type === 'yyyy-mm' ? (MONTH_DISPLAY_BY_LANG[language] || MONTH_DISPLAY_BY_LANG.ko) :
    type === 'yyyy-mm-dd hh:mm' ? `${DATE_DISPLAY_BY_LANG[language] || DATE_DISPLAY_BY_LANG.ko} HH:mm` :
    STORAGE_PATTERN_BY_TYPE[type];
  const storagePattern = STORAGE_PATTERN_BY_TYPE[type];

  return {
    // 2026-09-28: dateType을 예전엔 여기서 컬럼 최상위에 자동으로 심어줬으나(normalizeGridRows()가 "이
    // 컬럼은 날짜 컬럼"이라는 걸 알아채는 표식), ag-Grid가 모르는 최상위 속성이라 "invalid colDef property"
    // 경고가 났음. context.dateType으로 옮기려면 화면마다 이미 쓰고 있는 context(align/required 등)를
    // 이 함수가 통째로 덮어쓰게 되므로(스프레드라 부분병합 불가), 여기서는 더 이상 안 심어주고 화면에서
    // context: { dateType: '...' }로 직접 적어주는 방식으로 바꿈(normalizeGridRows()도 context.dateType을
    // 보도록 같이 수정함).
    cellEditor: AgGridDateCellEditor,
    cellEditorParams: { dateType: type },
    cellEditorPopup: true,
    cellEditorPopupPosition: 'over' as const,
    // 편집기/정렬/필터가 쓰는 "값" - 원본이 압축형이어도 편집기가 이해하는 ISO로 통일
    // (Date의 로컬 연/월/일을 직접 꺼내서 조립 - toISOString()은 UTC로 변환하면서 날짜가 하루 밀릴 수 있어 사용하지 않음)
    valueGetter: (params: any) => {
      const field = params.colDef?.field;
      const raw = field ? params.data?.[field] : undefined;
      const d = parseFlexibleDate(raw);
      if (!d) return raw ?? '';
      const pad = (n: number) => String(n).padStart(2, '0');
      const ymd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      return type === 'yyyy-mm-dd hh:mm' ? `${ymd} ${pad(d.getHours())}:${pad(d.getMinutes())}` : ymd;
    },
    // 화면표시(조회 상태) - 위 ISO값을 타입/언어에 맞는 표기로 재변환
    valueFormatter: (params: any) => formatDateByPattern(params.value, displayPattern),
    // 편집 완료 시 실제 저장되는 값 - 항상 타입 고유의 저장형식으로 변환해서 행에 반영
    valueSetter: (params: any) => {
      const field = params.colDef?.field;
      if (!field) return false;
      params.data[field] = formatDateByPattern(params.newValue, storagePattern);
      return true;
    },
  };
};

/**
 * 그리드에서 조회해온 원본 행 배열 중 dateColDef()가 붙은 컬럼들을 저장형식(YYYY-MM-DD 등)으로 미리
 * 정규화합니다. 사용자가 날짜 셀을 한 번도 편집하지 않은 행은 조회 시 받은 원본 형식(예: 시간까지 포함된
 * '1990-01-01T00:00:00.000Z')이 그대로 남아있는데, 이 상태로 저장 요청을 보내면 백엔드 TVP 컬럼 길이를
 * 넘겨서 SQL Server가 통째로 거부하는 문제가 있었음(K1201M1에서 실제 발생) - 저장 직전에 이 함수로 한 번
 * 정리해두면 화면/백엔드마다 매번 신경 쓰지 않아도 됨.
 *  - 사용법(fetchDBSave에서 saveRows 만들 때): const saveRows = normalizeGridRows(agGrid1Rows.filter(...), agGrid1Cols);
 */
// 컬럼그룹(ColGroupDef, children을 가진 컬럼 - 예: ZM901M1이 group_nm으로 묶은 상위 헤더)이 섞여 있어도
// 날짜정규화/필수검사가 그 안의 실제 컬럼(leaf)까지 보도록 재귀적으로 펼침(AgGrid.tsx의 findColDef와 같은 패턴).
const flattenCols = (cols: any[]): any[] => (cols || []).flatMap(c => (c?.children ? flattenCols(c.children) : [c]));

export const normalizeGridRows = (
  rows: any[],
  colsIn: { field?: string; dateType?: DateColDefType; context?: { dateType?: DateColDefType }; children?: any[] }[]
): any[] => {
  const cols = flattenCols(colsIn);
  // 2026-09-28: dateType을 context에 넣는 신규 방식도 지원(align/pk/required와 같은 이유) - context 우선,
  // 없으면 기존 최상위 dateType으로 폴백(과거에 저장된 화면은 안 건드려도 그대로 동작).
  const getDateType = (c: { dateType?: DateColDefType; context?: { dateType?: DateColDefType } }) => c.context?.dateType ?? c.dateType;
  const dateCols = cols.filter(c => getDateType(c) && c.field);
  if (dateCols.length === 0) return rows;
  return rows.map(row => {
    let changed = false;
    const next: any = { ...row };
    for (const col of dateCols) {
      const field = col.field as string;
      const raw = row[field];
      if (raw === null || raw === undefined || raw === '') continue;
      const normalized = formatDateByPattern(raw, STORAGE_PATTERN_BY_TYPE[getDateType(col) as DateColDefType]);
      if (normalized !== raw) {
        next[field] = normalized;
        changed = true;
      }
    }
    return changed ? next : row;
  });
};

/**
 * 그리드 저장 시 필수(required:true) 컬럼 검증 - 컬럼정의에 required:true(pk:true 포함, ag-Grid 표준 속성
 * 아님)만 붙여두면 자동으로 체크되어, 화면마다 "!row.a || !row.b || ..." 식으로 필드를 일일이 나열하지
 * 않아도 됩니다. 조회 화면의 requiredFields 배열 체크와 동일한 사고방식을 그리드 저장에 적용한 것.
 *  - cud가 'C'(신규) 또는 'U'(수정)인 행만 검사(삭제행은 값이 비어있어도 통과).
 *  - 값이 없는 첫번째 필수 컬럼의 headerName(번역된 라벨)을 반환, 문제없으면 null.
 *  - 사용법: const missing = getMissingRequiredField(saveRows, agGrid1Cols);
 *           if (missing) { showMsgBox({ msgText: `[${missing}] 은(는) 필수입니다.` }); return false; }
 */
export const getMissingRequiredField = (
  rows: any[],
  colsIn: { field?: string; headerName?: string; required?: boolean; pk?: boolean; context?: { required?: boolean; pk?: boolean }; children?: any[] }[]
): string | null => {
  const cols = flattenCols(colsIn);
  // 2026-09-28: required/pk를 colDef.context에 넣는 신규 방식도 지원(align과 같은 이유) - context 우선,
  // 없으면 기존 최상위 required/pk로 폴백(기존 화면은 안 건드려도 그대로 동작).
  const requiredCols = cols.filter(c => ((c.context?.required ?? c.required) || (c.context?.pk ?? c.pk)) && c.field);
  for (const row of rows) {
    if (row.cud !== 'C' && row.cud !== 'U') continue;
    for (const col of requiredCols) {
      const value = row[col.field as string];
      if (value === null || value === undefined || String(value).trim() === '') {
        return col.headerName || col.field || null;
      }
    }
  }
  return null;
};

/**
 * Y/N 같은 플래그성 데이터 컬럼을 체크박스로 표시/편집하도록 붙여줍니다(comboColDef/dateColDef와 같은 패턴).
 *  - cellEditor 없이 cellRenderer만 붙이면, 이 화면 공통설정인 singleClickEdit:true 때문에 셀을 클릭할 때
 *    ag-Grid가 기본 텍스트 편집기를 열어버려서(값이 "true"/"false" 문자열로 보임) 오히려 더 깨짐 -
 *    cellEditor: 'agCheckboxCellEditor'를 반드시 같이 지정해야 클릭 시 체크박스 편집기가 뜸.
 *  - valueGetter/valueSetter가 저장값(기본 'Y'/'N')과 체크박스가 쓰는 boolean 사이를 자동으로 변환해줍니다.
 *  - CheckboxEditorModule은 AgGrid.tsx에서 공통 등록되어 있어(2026-09-28) 화면에서 따로 등록할 필요는 없습니다.
 *  - 사용법: { field: 'psn_flg', editable: true, ...checkboxColDef() }                 // 기본 'Y'/'N'
 *           { field: 'is_active', editable: true, ...checkboxColDef(true, false) }    // 실제 boolean 저장컬럼인 경우
 */
export const checkboxColDef = (trueValue: any = 'Y', falseValue: any = 'N') => ({
  cellRenderer: 'agCheckboxCellRenderer',
  cellEditor: 'agCheckboxCellEditor',
  cellClass: 'ag-cell-checkbox-center',
  valueGetter: (params: any) => {
    const field = params.colDef?.field;
    const raw = field ? params.data?.[field] : undefined;
    return raw === trueValue;
  },
  valueSetter: (params: any) => {
    const field = params.colDef?.field;
    if (!field) return false;
    params.data[field] = params.newValue ? trueValue : falseValue;
    return true;
  },
});

/**
 * 콤보(useComboBox/useComboBoxSite 등)로 받은 옵션 목록을 MUI TextField(select)의 <MenuItem> 목록으로 변환합니다.
 *  - 매번 반복되던 {options.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)} 대신 사용.
 *  - 사용법: <TextField select ...>{comboMenuItems(cbBpGrp)}</TextField>
 */
export const comboMenuItems = (options: { value: any; label: string }[]) =>
  (options || []).map(opt => (
    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
  ));

/**
 * axios 에러에서 표시할 메세지를 추출합니다.
 *  - 백엔드가 sendCodedMessage()로 ZA_MESSAGE를 조회해 이미 번역된 msgText를 응답에 담아 보내므로,
 *    프론트는 별도 번역 없이 그 값을 그대로 사용.
 *  - msgText가 없으면(네트워크 오류 등 응답 자체가 없는 경우) 서버의 기본 메세지(error/message)로 대체.
 *  - 그마저 없으면 fallback 문자열 사용.
 *  사용법: showMsgBox({ msgText: getMsgText(error, ddKey('loginFailed') || '로그인 실패') })
 */
export const getMsgText = (
  error: any,
  fallback: string = '오류가 발생했습니다.'
): string => {
  const data = error?.response?.data;
  return data?.msgText || data?.error || data?.message || fallback;
};

/**
 * axios 에러에서 ZA_MESSAGE의 msgType(warning/error 등)을 추출합니다.
 *  - showMsgBox의 msgType(아이콘 결정)에 그대로 사용.
 *  사용법: showMsgBox({ msgType: getMsgType(error) })
 */
export const getMsgType = (error: any, fallback: string = 'error'): string => {
  return error?.response?.data?.msgType || fallback;
};

/**
 * 파일 크기(byte)를 사람이 읽기 좋은 단위(B/KB/MB)로 변환합니다.
 *  - 첨부파일 카드(X1001M1/X1002Q1 등)에서 공통으로 사용.
 */
export const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * 파일 확장자별 아이콘(FontAwesome)/색상을 반환합니다.
 *  - 첨부파일 카드에서 파일 종류를 한눈에 알아볼 수 있게 표시하는 용도로 공통 사용.
 *  사용법: const fi = getFileIcon(fileName); <i className={`fas ${fi.icon}`} style={{ color: fi.color }} />
 */
export const getFileIcon = (fileName: string): { icon: string; color: string } => {
  const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
  if (ext === 'pdf') return { icon: 'fa-file-pdf', color: '#dc2626' };
  if (['doc', 'docx'].includes(ext)) return { icon: 'fa-file-word', color: '#2563eb' };
  if (['xls', 'xlsx', 'csv'].includes(ext)) return { icon: 'fa-file-excel', color: '#16a34a' };
  if (['ppt', 'pptx'].includes(ext)) return { icon: 'fa-file-powerpoint', color: '#ea580c' };
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) return { icon: 'fa-file-image', color: '#9333ea' };
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return { icon: 'fa-file-zipper', color: '#a16207' };
  if (['mp3', 'wav', 'aac', 'flac'].includes(ext)) return { icon: 'fa-file-audio', color: '#0891b2' };
  if (['mp4', 'avi', 'mov', 'wmv', 'mkv'].includes(ext)) return { icon: 'fa-file-video', color: '#0891b2' };
  if (ext === 'txt' || ext === 'hwp') return { icon: 'fa-file-lines', color: ext === 'hwp' ? '#2563eb' : '#64748b' };
  return { icon: 'fa-file', color: '#64748b' };
};
